import React from 'react';
import { PrintableDocument, PrintStyles } from '../ui/PrintableDocument';
import { formatCurrency, formatDate } from '../../lib/utils';

interface PrintablePaymentProps {
  payment: any;
  isVisible?: boolean;
}

export const PrintablePayment: React.FC<PrintablePaymentProps> = ({
  payment,
  isVisible = false
}) => {
  if (!payment) return null;

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'cash':
        return 'bg-success-100 text-success-700 border-success-200';
      case 'card':
        return 'bg-primary-100 text-primary-700 border-primary-200';
      case 'bank_transfer':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'upi':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'cheque':
        return 'bg-warning-100 text-warning-700 border-warning-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <>
      <PrintStyles />
      <div className={`${isVisible ? 'block' : 'hidden'} print:block`}>
        <PrintableDocument type="payment" data={payment}>
          {/* Payment Details Section */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            {/* Payment From */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                PAYMENT FROM
              </h3>
              <div className="space-y-2">
                <p className="font-semibold text-gray-900">{payment.invoice?.client?.name}</p>
                {payment.invoice?.client?.company && (
                  <p className="text-gray-700">{payment.invoice.client.company}</p>
                )}
                <p className="text-gray-600">{payment.invoice?.client?.email}</p>
                {payment.invoice?.client?.phone && (
                  <p className="text-gray-600">{payment.invoice.client.phone}</p>
                )}
              </div>
            </div>

            {/* Payment Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                PAYMENT DETAILS
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Date:</span>
                  <span className="font-medium">{formatDate(payment.payment_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Recorded:</span>
                  <span className="font-medium">{formatDate(payment.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Method:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${getMethodColor(payment.method)}`}>
                    {payment.method.replace('_', ' ')}
                  </span>
                </div>
                {payment.reference_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-medium">{payment.reference_id}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">PAYMENT SUMMARY</h3>
            <div className="bg-success-50 border border-success-200 rounded-lg p-6">
              <div className="text-center">
                <p className="text-success-600 text-sm font-medium mb-2">Amount Paid</p>
                <p className="text-4xl font-bold text-success-700">{formatCurrency(Number(payment.amount))}</p>
              </div>
            </div>
          </div>

          {/* Invoice Information */}
          {payment.invoice && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">INVOICE INFORMATION</h3>
              <table className="document-table w-full">
                <tbody>
                  <tr>
                    <td className="py-3 px-4 font-medium text-gray-700">Invoice Number:</td>
                    <td className="py-3 px-4">{payment.invoice.invoice_number}</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-gray-700">Invoice Total:</td>
                    <td className="py-3 px-4 font-semibold">{formatCurrency(Number(payment.invoice.total))}</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-gray-700">Payment Amount:</td>
                    <td className="py-3 px-4 font-semibold text-success-600">{formatCurrency(Number(payment.amount))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Notes */}
          {payment.notes && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">NOTES</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-700">{payment.notes}</p>
              </div>
            </div>
          )}

          {/* Receipt Footer */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Payment Confirmation</h4>
            <p className="text-blue-800 text-sm">
              This receipt confirms that payment has been received and processed. 
              Please keep this receipt for your records.
            </p>
          </div>
        </PrintableDocument>
      </div>
    </>
  );
};